import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Input,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import { ChartModule } from "primeng/chart";
import { isPlatformBrowser } from "@angular/common";
import { SharedService } from "../../../../../core/services/shared.service";
import { ReadyToBillDto } from "../../../../../core/models/reports/readyToBillDto";

@Component({
  selector: "jni-pie-chart",
  imports: [ChartModule],
  templateUrl: "./pie-chart.component.html",
  styleUrl: "./pie-chart.component.scss",
})
export class PieChartComponent implements OnInit {
  @Input() type = "";
  @Input() recordList!: ReadyToBillDto[] | undefined;
  @Input() title = "";
  @Input() xAxisTitle = "";
  @Input() yAxisTitle = "";

  data!: {
    labels: string[];
    datasets: {
      label?: string;
      backgroundColor?: string | string[];
      borderColor?: string;
      hoverBackgroundColor?: string | string[];
      data?: number[];
      type?: string;
      borderWidth?: number;
      fill?: boolean;
      tension?: number;
    }[];
  };
  options!: {
    plugins: {
      legend: {
        labels: {
          usePointStyle: boolean;
          color: string;
        };
        position: string;
      };
      title?: {
        display: boolean;
        text: string;
        font: { size: number; weight: string };
      };
    };
  };

  platformId = inject(PLATFORM_ID);

  private sharedService = inject(SharedService);

  bgColor!: string;

  constructor(private cd: ChangeDetectorRef) {
    effect(() => {
      const currentTheme = this.sharedService.getTheme()();
      this.bgColor = currentTheme === "dark" ? "#212630" : "#FFFFFF";
      this.ngOnInit();
    });
  }

  ngOnInit() {
    this.initChart();
  }
  getTop10CustomersBySell(response: ReadyToBillDto[] | undefined) {
    const grouped: Record<string, { totalSell: number; clientName: string }> =
      {};

    response?.forEach(order => {
      const account = order?.customerAccount;
      const sell = order?.totalSell ?? 0;
      if (!grouped[account]) {
        grouped[account] = {
          totalSell: 0,
          clientName: order.clientName,
        };
      }

      grouped[account].totalSell += sell;
    });

    // Convert to array and sort descending by totalSell
    const result = Object.entries(grouped)
      .map(([customerAccount, { totalSell, clientName }]) => ({
        customerAccount,
        clientName,
        totalSell,
      }))
      .sort((a, b) => b.totalSell - a.totalSell)
      .slice(0, 10); // Top 10
    return result;
  }
  initChart() {
    if (isPlatformBrowser(this.platformId)) {
      const documentStyle = getComputedStyle(document.documentElement);
      const textColor = documentStyle.getPropertyValue("--p-h2color-color");

      const data = this.getTop10CustomersBySell(this.recordList);

      const labels = data.map(item => item.clientName);
      const values = data.map(item => item.totalSell);

      // Predefined color variables for up to 10 entries
      const bgColors = [
        "--p-cyan-500",
        "--p-orange-500",
        "--p-gray-500",
        "--p-purple-500",
        "--p-green-500",
        "--p-pink-500",
        "--p-yellow-500",
        "--p-teal-500",
        "--p-indigo-500",
        "--p-red-500",
      ].map(varName => documentStyle.getPropertyValue(varName));

      const hoverBgColors = [
        "--p-cyan-400",
        "--p-orange-400",
        "--p-gray-400",
        "--p-purple-400",
        "--p-green-400",
        "--p-pink-400",
        "--p-yellow-400",
        "--p-teal-400",
        "--p-indigo-400",
        "--p-red-400",
      ].map(varName => documentStyle.getPropertyValue(varName));

      this.data = {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: bgColors.slice(0, data.length),
            hoverBackgroundColor: hoverBgColors.slice(0, data.length),
          },
        ],
      };

      this.options = {
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
              color: textColor,
            },
            position: "right",
          },
          title: {
            display: true,
            text: this.title,
            font: { size: 18, weight: "bold" },
          },
        },
      };

      this.cd.markForCheck();
    }
  }
}
